import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultLayout from '../../components/Layout/DefaultLayout';
import {
  AboutCampaignAndCharitySection,
  DonateButton,
  EventDetails,
  FacebookShare,
  FeatureImage,
  PageOwnerTitle,
  PageSection,
  Story,
} from '../../components';

import config from '../../client/config';
import CampaignsHelmet from '../../components/Helmet/CampaignsHelmet';
import CharityRegNumber from '../../components/CharityRegNumber/CharityRegNumber';
import SocialButtonsListWrapper from '../../components/SocialButtons/SocialButtonsListWrapper';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import Page from '../../components/Page/Page';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import CampaignLogo from '../../components/CampaignLogo/CampaignLogo';
import StickySummary from '../../components/StickySummary/StickySummary';
import SuccessMessage from '../../components/SuccessMessage/SuccessMessage';
import WeDidItHeader from '../../components/WeDidItHeader/WeDidItHeader';
import Editbar from '../../components/Editbar/Editbar';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import QRModal from '../../components/QRModal/QRModal';
import ClosingDate from '../../components/ClosingDate/ClosingDate';

import '../Pages.scss';
import styles from './AuctionPage.scss';
import { Banner } from '../../components/Banner/Banner';
import PageAlert from '../../components/PageAlert/PageAlert';
import gql from 'graphql-tag';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { AuctionLot, AuctionPageQuery } from '../../../types/__generated__';
import AuctionTotaliserWrapper from '../../components/Auction/AuctionTotaliserWrapper/AuctionTotaliserWrapper';
import AuctionTotals from '../../components/Auction/AuctionTotals/AuctionTotals';
import AuctionLotsList from '../../components/Auction/AuctionLotsList/AuctionLotsList';

export const DOCUMENT = gql`
  query AuctionPage($slug: Slug!) {
    getAuctionLots(slug: $slug) {
      legacyId
      title
      description
      image {
        url
      }
      highestBid {
        value
        currencyCode
      }
      lotValue {
        value
      }
      isEnded
    }
  }
`;

export const AuctionPage: React.FunctionComponent = () => {
  const ctaRef = useRef<HTMLDivElement>(null);
  const basePageData = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });

  const { data, refetch } = useFullPageQuery<AuctionPageQuery>(DOCUMENT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      slug: '12345',
    },
  });

  const [lotsStore, updateLotsStore] = useState<AuctionLot[] | undefined>(
    undefined,
  );

  useEffect(() => {
    const interval = setInterval(() => {
      // console.log('Requesting updated lots!');
      refetch();
    }, 15000);
    return () => clearInterval(interval);
  }, [refetch]);

  const auctionIncome = lotsStore
    ? lotsStore.reduce(
        (total, lot) => total + parseFloat(lot.highestBid.value),
        0,
      )
    : undefined;

  // let auctionIncome: number | undefined = undefined;
  // if (lotsStore) {
  //   lotsStore.forEach(lot => {
  //     auctionIncome = auctionIncome
  //       ? auctionIncome + parseFloat(lot.highestBid.value)
  //       : parseFloat(lot.highestBid.value);
  //   });
  // }

  // console.log('AuctionPage');
  // console.log(lotsStore);
  // console.log(auctionIncome);

  useEffect(() => {
    if (data) {
      // console.log('Received updated lots!');
      updateLotsStore(data.getAuctionLots as AuctionLot[]);
    }
  }, [data]);

  const pageData = basePageData?.data?.page;

  if (!pageData) {
    return null;
  }

  const { legacyId } = pageData;
  const shareSubject = '';
  const shareMessage = '';
  return (
    <Page>
      <IsQRCode>
        <QRModal ownerName={pageData?.title} />
      </IsQRCode>
      <CampaignsHelmet />
      <PageAlert />
      <Banner />
      <Editbar
        adminLink={`${config.baseUri}/campaign/manage/edit/${legacyId}`}
      />
      <SuccessMessage />
      <WeDidItHeader />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <CampaignLogo />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <AuctionTotaliserWrapper additionalIncome={auctionIncome} />
              <AuctionTotals additionalIncome={auctionIncome} />
            </>
          ),
          callsToAction: (
            <div className={styles.ctaWrapper} ref={ctaRef}>
              <DonateButton className="jg-space-mrsm jg-space-mrn@lg jg-space-mbsm@lg" />
              <FacebookShare showPrefix />
            </div>
          ),
          titlesSupplementary: (
            <>
              <ClosingDate />
              <EventDetails />
              <CharityRegNumber />
            </>
          ),
          causeMini: null,
          main: (
            <>
              <PageSection title={'Auction Details'} hasBorder>
                <Story />
                <AuctionLotsList lots={lotsStore} />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <SocialButtonsListWrapper
                  subject={shareSubject}
                  message={shareMessage}
                />
              </PageSection>
            </>
          ),
          aside: null,
          contentFooterPrimary: (
            <AboutCampaignAndCharitySection withExternalUrl={true} />
          ),
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={[
                'directDonationAmount',
                'fundraiserRaisedAmount',
                'offlineAmount',
              ]}
            />
          ),
        }}
      />
      <StickySummary ref={ctaRef} />
    </Page>
  );
};

AuctionPage.displayName = 'CampaignPage';

export default AuctionPage;
